import React from "react";
import { DatePicker, Input, Select } from "antd";
import moment from "moment";

import {
  useInvestmentProductSubtypeDetails,
  useInvestmentProductSubtypes,
  useInvestmentProductTypes,
} from "../../../../custom-hooks/useDebtFund";
import FormSection from "./FormSection";
import Field from "./Field";
import useInvestmentPlanTypes from "../../../../custom-hooks/useInvestmentPlanTypes";
import styles from "./fundForm.module.scss";

export type TInvestmentDetailsValue = {
  name: string;
  productTypeId?: number | null;
  investmentProductTypeId: number | null;
  investmentProductSubTypeId: number | null;
  investmentProductSubTypeDetailId: number | null;
  startDate: string;
  endDate: string;
  description: string;
  objective: string;
  issuers: string;
  targetInvestors: string;
};

type TLocks = Partial<Record<keyof TInvestmentDetailsValue, string>>;

interface IInvestmentDetailsSection {
  value: TInvestmentDetailsValue;
  onChange: (patch: Partial<TInvestmentDetailsValue>) => void;
  /** Field name to the reason it cannot be edited on a live fund. */
  locks?: TLocks;
  step?: string;
}

const DATE_FORMAT = "DD MMM YYYY";
const ISO = "YYYY-MM-DD";

/** What this fund is, and how long it accepts subscriptions. */
export default function InvestmentDetailsSection({
  value,
  onChange,
  locks = {},
  step,
}: IInvestmentDetailsSection) {
  const { data: types } = useInvestmentProductTypes();
  const { data: subtypes } = useInvestmentProductSubtypes(
    value.investmentProductTypeId as number,
  );
  const { data: subtypeDetails } = useInvestmentProductSubtypeDetails(
    value.investmentProductSubTypeId as number,
  );
  const { data: planTypes, isLoading: planTypesLoading } =
    useInvestmentPlanTypes();

  const asDate = (raw: string) => (raw ? moment(raw) : null);

  return (
    <FormSection
      id="investment-details"
      title="Investment details"
      subtitle="What this fund is, and how long it accepts subscriptions."
      meta={step}
    >
      <div className={styles.grid}>
        <Field required label="Investment name" lockedReason={locks.name}>
          <Input
            size="large"
            placeholder="e.g. Advancly Flexy Save"
            value={value.name}
            disabled={Boolean(locks.name)}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </Field>

        <Field
          label="Plan type"
          optional
          hint="Only for the savings plans. Leave it empty for any other investment product."
          lockedReason={locks.productTypeId}
        >
          <Select
            size="large"
            style={{ width: "100%" }}
            placeholder="Not a savings plan"
            // The list is the backend's to extend, so it is fetched rather
            // than written down here.
            loading={planTypesLoading}
            allowClear
            value={value.productTypeId ?? undefined}
            disabled={Boolean(locks.productTypeId)}
            onChange={(next) =>
              onChange({
                productTypeId:
                  next === undefined || next === null ? null : Number(next),
              })
            }
            options={(planTypes ?? []).map((type) => ({
              value: type.id,
              // `name` is the enum spelling; `description` is the words.
              label: type.description || type.name,
            }))}
          />
        </Field>

        <Field required label="Investment type">
          <Select
            size="large"
            style={{ width: "100%" }}
            placeholder="Select"
            value={value.investmentProductTypeId ?? undefined}
            onChange={(next) =>
              onChange({
                investmentProductTypeId: Number(next),
                investmentProductSubTypeId: null,
                investmentProductSubTypeDetailId: null,
              })
            }
            options={(types ?? []).map((type: any) => ({
              value: type.id,
              label: type.name,
            }))}
          />
        </Field>

        <div className={styles.pair}>
          <Field label="Subtype" optional>
            <Select
              size="large"
              style={{ width: "100%" }}
              placeholder="Select"
              disabled={!value.investmentProductTypeId}
              value={value.investmentProductSubTypeId ?? undefined}
              onChange={(next) =>
                onChange({
                  investmentProductSubTypeId: Number(next),
                  investmentProductSubTypeDetailId: null,
                })
              }
              options={(subtypes ?? []).map((sub: any) => ({
                value: sub.subTypeId,
                label: sub.subTypeName,
              }))}
            />
          </Field>

          <Field label="Subtype detail" optional>
            <Select
              size="large"
              style={{ width: "100%" }}
              placeholder={
                value.investmentProductSubTypeId
                  ? "Select"
                  : "Select a subtype first"
              }
              disabled={!value.investmentProductSubTypeId}
              value={value.investmentProductSubTypeDetailId ?? undefined}
              onChange={(next) =>
                onChange({ investmentProductSubTypeDetailId: Number(next) })
              }
              options={(subtypeDetails ?? []).map((detail: any) => ({
                value: detail.subTypeDetailId,
                label: detail.subTypeDetail,
              }))}
            />
          </Field>
        </div>

        <Field
          required
          label="Subscription opens"
          lockedReason={locks.startDate}
        >
          <DatePicker
            size="large"
            style={{ width: "100%" }}
            format={DATE_FORMAT}
            disabled={Boolean(locks.startDate)}
            value={asDate(value.startDate)}
            onChange={(date) =>
              onChange({ startDate: date ? date.format(ISO) : "" })
            }
          />
        </Field>

        <Field required label="Subscription closes">
          <DatePicker
            size="large"
            style={{ width: "100%" }}
            format={DATE_FORMAT}
            value={asDate(value.endDate)}
            onChange={(date) =>
              onChange({ endDate: date ? date.format(ISO) : "" })
            }
          />
        </Field>

        <Field label="About this fund" optional className={styles.span2}>
          <Input.TextArea
            rows={3}
            placeholder="A sentence a customer would understand."
            value={value.description}
            disabled={Boolean(locks.description)}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </Field>

        <Field
          label="Investment objective"
          optional
          lockedReason={locks.objective}
        >
          <Input
            size="large"
            value={value.objective}
            disabled={Boolean(locks.objective)}
            onChange={(e) => onChange({ objective: e.target.value })}
          />
        </Field>

        <div className={styles.pair}>
          <Field label="Issuer" optional lockedReason={locks.issuers}>
            <Input
              size="large"
              value={value.issuers}
              disabled={Boolean(locks.issuers)}
              onChange={(e) => onChange({ issuers: e.target.value })}
            />
          </Field>

          <Field
            label="Target investors"
            optional
            lockedReason={locks.targetInvestors}
          >
            <Input
              size="large"
              value={value.targetInvestors}
              disabled={Boolean(locks.targetInvestors)}
              onChange={(e) => onChange({ targetInvestors: e.target.value })}
            />
          </Field>
        </div>
      </div>
    </FormSection>
  );
}
